'use client';

import { type FlexProps } from '@ironhack/design-system2/components/flex';
import {
  LinkBox,
  LinkOverlay,
} from '@ironhack/design-system2/components/link-box';
import {
  SuperCard,
  type SuperCardSize,
} from '@ironhack/design-system2/components/supercard';
import { Text } from '@ironhack/design-system2/components/text';
import { useBreakpointValue } from '@ironhack/design-system2/hooks';
import { render } from 'datocms-structured-text-to-plain-text';
import { findLast, last, propEq } from 'ramda';
import { type ReactElement } from 'react';
import { useMedia } from 'react-use';

import { type BasePageParams } from '@/app/urls/url-mapping';
import {
  Button,
  type DatoButtonLikeBlock,
  getButtonTarget,
} from '@/app/components/client/button';
import { getHref } from '@/app/components/client/link';
import { StructuredContent } from '@/app/components/client/structured-content';
import { CmsImage } from '@/app/components/server/cms-image';
import { usePageContext } from '@/contexts/page-context/use-page-context';
import { type DatoCardBlock } from '@/lib/datocms/types';

type ButtonData = DatoButtonLikeBlock;

const getPrimaryButtonUrl = (
  buttons: ButtonData[],
  locale: Pick<BasePageParams, 'language' | 'region'>
): string | null => {
  if (buttons.length === 0) return null;

  const primaryButton =
    findLast(propEq('variant', 'primary') as (b: ButtonData) => boolean)(
      buttons
    ) ?? last(buttons);
  if (!primaryButton) return null;

  const url = getHref(getButtonTarget(primaryButton), locale);
  return url;
};

type Size = Exclude<SuperCardSize, 'full'>;

const DEFAULT_MEDIA_SIZE: Size = '4xl';

const useMediaSize = (size: Size): Size => {
  const isXl = useMedia(`(max-width: ${272 + 32}px)`, false);
  const is2xl = useMedia(`(max-width: ${328 + 32}px)`, false);
  const is3xl = useMedia(`(max-width: ${363 + 32}px)`, false);
  const base = isXl ? 'xl' : is2xl ? '2xl' : is3xl ? '3xl' : DEFAULT_MEDIA_SIZE;

  const mediaSize = useBreakpointValue<Size>({
    base,
    sm: size,
  });

  return mediaSize ?? DEFAULT_MEDIA_SIZE;
};

type Props = {
  card: DatoCardBlock;
  swapMobileMedia?: boolean;
} & FlexProps;

export const Card = (props: Props): ReactElement => {
  const { card, swapMobileMedia = false, ...flexProps } = props;
  const {
    button: buttons = [],
    content,
    contentAsPlainText = false,
    hexagonLeft,
    hexagonRight,
    leftBadge: leftBadgeBlock,
    logo,
    note,
    maxContentLines,
    progress,
    rightBadge: rightBadgeBlock,
    size = '2xl',
    subtitle,
    tags,
    title,
    titleMaxLines,
  } = card;

  const mediaSize = size === 'full' ? DEFAULT_MEDIA_SIZE : size;
  const swappedMediaSize = useMediaSize(mediaSize);
  const finalMediaSize = swapMobileMedia ? swappedMediaSize : mediaSize;
  const media = card[`media${finalMediaSize.toUpperCase() as Uppercase<Size>}`];

  const {
    params: { language, region },
  } = usePageContext();

  const leftBadge = leftBadgeBlock?.[0];
  const rightBadge = rightBadgeBlock?.[0];

  const hasBadges = Boolean(
    hexagonLeft || hexagonRight || leftBadge || rightBadge
  );
  const hasHeader = Boolean(media || hasBadges);
  const hasTags = tags && tags.length > 0;
  const BadgesComponent = media ? SuperCard.MediaBadges : SuperCard.Badges;
  const primaryButtonUrl = getPrimaryButtonUrl(buttons, { language, region });

  return (
    <SuperCard as={LinkBox} size={size} {...flexProps}>
      {hasHeader && (
        <SuperCard.Header>
          {media && (
            <SuperCard.Media>
              <CmsImage
                data={media.responsiveImage}
                objectFit="cover"
                objectPosition="center"
                style={{ height: '100%' }}
              />
            </SuperCard.Media>
          )}
          {hasBadges && (
            <BadgesComponent
              {...(hexagonLeft && {
                leftHexagon: hexagonLeft.backgroundColor,
              })}
              {...(leftBadge && {
                primaryTag: {
                  text: leftBadge.text,
                  colorScheme: leftBadge.background,
                },
              })}
              {...(hexagonRight && {
                rightHexagon: hexagonRight.backgroundColor,
              })}
              {...(rightBadge && {
                secondaryTag: {
                  text: rightBadge.text,
                  colorScheme: rightBadge.background,
                },
              })}
            />
          )}
        </SuperCard.Header>
      )}
      <SuperCard.Body flexGrow={1}>
        {progress && (
          <SuperCard.Progress
            label={`PROGRESS ${progress}%`}
            value={progress}
          />
        )}
        {title &&
          (primaryButtonUrl ? (
            <LinkOverlay href={primaryButtonUrl} isExternal>
              <SuperCard.Title
                as="h3"
                {...(titleMaxLines && { noOfLines: titleMaxLines })}
              >
                {title}
              </SuperCard.Title>
            </LinkOverlay>
          ) : (
            <SuperCard.Title
              as="h3"
              {...(titleMaxLines && { noOfLines: titleMaxLines })}
            >
              {title}
            </SuperCard.Title>
          ))}
        {subtitle && <SuperCard.Subtitle>{subtitle}</SuperCard.Subtitle>}
        {note && <SuperCard.Note>{note}</SuperCard.Note>}
        {hasTags && <SuperCard.Tags tags={tags.map((t) => t.tag)} />}
        {(content || logo) && (
          <SuperCard.Content gap={2}>
            {logo && <CmsImage data={logo.responsiveImage} />}
            {content &&
              (contentAsPlainText ? (
                <Text noOfLines={maxContentLines}>{render(content)}</Text>
              ) : (
                <StructuredContent
                  data={content}
                  {...(maxContentLines && { noOfLines: maxContentLines })}
                />
              ))}
          </SuperCard.Content>
        )}
      </SuperCard.Body>
      {primaryButtonUrl && (
        // zIndex is needed for the buttons to be over the LinkBox
        <SuperCard.Actions direction="column" gap={2} zIndex={1}>
          {buttons.map((button) => (
            <Button key={button.text} size="s" w="full" {...button} />
          ))}
        </SuperCard.Actions>
      )}
    </SuperCard>
  );
};
